import React, { JSX, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router'
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useEntitlements } from 'context/plan'
import { useYouTubeConnection } from 'hooks/useYouTubeConnection'
import { useTabReturnRecheck } from 'hooks/useTabReturnRecheck'
import { CONNECTIONS_PATH } from 'lib/paths'
import {
  YOUTUBE_BETA_BANNER_COPY as COPY,
  readYouTubeBannerClosed,
  saveYouTubeBannerClosed,
  youTubeBannerEligible,
  youTubeBannerVisible
} from 'lib/youtubeBetaBanner'

const FOCUS_RING = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand'

export const YouTubeBetaBanner = (): JSX.Element | null => {
  const { status, account } = useEntitlements()
  const { pathname } = useLocation()
  const accountId = account?.accountId ?? null
  const storedClosed = useMemo(
    () => accountId != null && readYouTubeBannerClosed(accountId),
    [accountId]
  )
  const [closedNow, setClosedNow] = useState(false)

  const eligible = youTubeBannerEligible({
    entitlementsReady: status === 'ready' && accountId != null,
    invited: account?.youtubeChatInvited === true,
    closed: storedClosed || closedNow,
    onConnectionsPage: pathname.startsWith(CONNECTIONS_PATH)
  })
  if (!eligible || accountId == null) return null

  const handleClose = (): void => {
    saveYouTubeBannerClosed(accountId)
    setClosedNow(true)
  }
  return <YouTubeBetaBannerBody onClose={handleClose} />
}

const YouTubeBetaBannerBody = ({ onClose }: { onClose: () => void }): JSX.Element | null => {
  const [refreshKey, setRefreshKey] = useState(0)
  const lastReadAtRef = useRef<number | null>(Date.now())
  const { youtubeLinked } = useYouTubeConnection(refreshKey)

  useTabReturnRecheck({
    enabled: youtubeLinked !== true,
    recheck: () => setRefreshKey((k) => k + 1),
    lastCheckAtRef: lastReadAtRef
  })

  if (!youTubeBannerVisible(true, youtubeLinked)) return null

  return (
    <div className='px-4 pt-4'>
      <div
        role='status'
        data-testid='youtube-beta-banner'
        className='flex items-start gap-3 rounded-md border border-brand/30 bg-brand/10 p-3 text-sm text-dm-text-primary'
      >
        <SparklesIcon className='size-5 shrink-0 text-brand' aria-hidden='true' />
        <p className='flex-1 leading-relaxed'>
          <span className='font-semibold'>{COPY.headline}</span>{' '}
          <span className='text-dm-text-secondary'>{COPY.body}</span>{' '}
          <Link
            to={CONNECTIONS_PATH}
            data-testid='youtube-beta-banner-link'
            className={`rounded-sm font-semibold text-brand underline hover:text-brand/80 ${FOCUS_RING}`}
          >
            {COPY.link}
          </Link>
        </p>
        <button
          type='button'
          onClick={onClose}
          aria-label={COPY.dismiss}
          title={COPY.dismiss}
          data-testid='youtube-beta-banner-dismiss'
          className={`-m-1 shrink-0 rounded p-1 text-dm-text-accent transition-colors hover:bg-white/5 hover:text-dm-text-primary ${FOCUS_RING}`}
        >
          <XMarkIcon className='size-4' aria-hidden='true' />
        </button>
      </div>
    </div>
  )
}
